// services/employeesApi.ts
import apiClient from "./apiClient";

export interface Employee {
  id: number;
  ownerName: string;
  email: string;
  phone: string;
  userTypeId: number;
  isVerified: boolean;
  organizationId: number;
  designation: string;
  employeeCode: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CreateEmployeePayload {
  ownerName: string;
  email: string;
  password: string;
  phone: string;
  designation: string;
  employeeCode: string; // auto-generated by frontend (timestamp)
}

export interface UpdateEmployeePayload {
  ownerName?: string;
  email?: string;
  phone?: string;
  designation?: string;
  isActive?: 0 | 1; // for status toggle
  password?: string; // optional for password change
}

export interface EmployeesResponse {
  employees: Employee[];
  pagination: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface Permission {
  id: number;
  name: string;
  code: string;
  granted: boolean;
}

export interface UpdatePermissionPayload {
  permissionId: number;
  granted: boolean;
}

// Helper: generate unique employee code using timestamp
export const generateEmployeeCode = (): string => {
  return `EMP${Date.now()}`;
};

export const employeesApi = {
  // Get all employees (paginated & search)
  getAll: async (
    page: number,
    limit: number,
    search: string,
  ): Promise<EmployeesResponse> => {
    const response = await apiClient.get("/employees", {
      params: { page, limit, search: search.trim() || undefined },
    });
    return response.data;
  },

  // Get single employee
  getById: async (id: number): Promise<{ employee: Employee }> => {
    const response = await apiClient.get(`/employees/${id}`);
    return response.data;
  },

  // Create new employee
  create: async (
    data: CreateEmployeePayload,
  ): Promise<{ message: string; employee: Employee }> => {
    const response = await apiClient.post("/employees", data);
    return response.data;
  },

  // Update employee
  update: async (
    id: number,
    data: UpdateEmployeePayload,
  ): Promise<{ message: string; employee: Employee }> => {
    const response = await apiClient.put(`/employees/${id}`, data);
    return response.data;
  },

  // Delete employee
  delete: async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/employees/${id}`);
    return response.data;
  },

  // 🔐 Get employee permissions (Owner only)
  getPermissions: async (
    employeeId: number,
  ): Promise<{ permissions: Permission[] }> => {
    const response = await apiClient.get(
      `/employees/${employeeId}/permissions`,
    );
    return response.data;
  },

  // 🔐 Update employee permissions (Owner only)
  updatePermissions: async (
    employeeId: number,
    permissions: UpdatePermissionPayload[],
  ): Promise<{ message: string }> => {
    const response = await apiClient.put(
      `/employees/${employeeId}/permissions`,
      { permissions },
    );
    return response.data;
  },
};
